import { fromUnixTime } from 'date-fns';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { OrgStatBadge } from '#/components/OrgStatBadge/OrgStatBadge';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaOrgHit } from '#/modules/search/algolia/types/SearchAlgoliaOrgHit';
import { SearchHitImage } from '#/modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitFloatingSaveButton } from '#/modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitHoverEffect,
  SearchHitImageHolder,
} from '#/modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from '#/modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from '#/modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';
import type { SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { AREAS_OF_FOCUS } from '#/utils/constants/general/areasOfFocus';
import { formatRelativeToNow } from '#/utils/date';
import { shuffleArray } from '#/utils/functional/array';

import { SearchOrgHitInfo } from './SearchOrgHitInfo';

type Props = {
  hit: SearchAlgoliaOrgHit;
  href: string;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  highlighted: boolean;
};

export function SearchOrgHit({
  hit,
  href,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  highlighted,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const pillItems: string[] = useMemo(() => {
    let trimmedAreasOfFocus = hit.areasOfFocus;
    if (hit.areasOfFocus.length > 3) {
      trimmedAreasOfFocus = shuffleArray(hit.areasOfFocus).slice(0, 3);
    }

    return trimmedAreasOfFocus.map(
      (areaOfFocus) => AREAS_OF_FOCUS(i18n)[areaOfFocus],
    );
  }, [i18n, hit]);

  const orgStat = useMemo(() => {
    if (hit.hiring) {
      return (
        <OrgStatBadge iconName="star-check" text={i18n('Actively Hiring')} />
      );
    }
    if (hit.hasUpcomingEvents) {
      return (
        <OrgStatBadge iconName="calendar" text={i18n('Upcoming Events')} />
      );
    }
    if (hit.seekingVolunteers) {
      return (
        <OrgStatBadge iconName="group" text={i18n('Seeking Volunteers')} />
      );
    }
    return null;
  }, [i18n, hit]);

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (hit.published) {
      stats.push(
        <BodyMicro lineHeight={1}>
          {i18n('Posted %(postedAt)s', {
            postedAt: formatRelativeToNow({
              date: fromUnixTime(hit.published),
              locale,
              dtOverride: appConfig.idealist.dtOverride,
            }),
          })}
        </BodyMicro>,
      );
    }

    return stats;
  }, [i18n, locale, hit, appConfig]);

  return (
    <SearchHitHolder
      id={getSearchHitElementId(hit.objectID)}
      data-qa-id="search-result"
    >
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitHoverEffect />

          <SearchHitImageHolder $hiddenOnMobile={!hit.logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={null}
              image={null}
              source={null}
              name={hit.name}
              logo={hit.logo}
            />
          </SearchHitImageHolder>

          <SearchOrgHitInfo
            hiring={hit.hiring}
            listingStats={listingStats}
            orgStat={orgStat}
            name={hit.name}
            orgType={hit.orgType}
            showLabel={showLabel}
            pillItems={pillItems}
          />
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
